import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Mail, Phone, MessageCircle, MapPin, Building2 } from "lucide-react";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

export default function ContactPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: "#F5F5F0" }} data-testid="contact-page">
      {/* ── HEADER ── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 border-b"
        style={{ backgroundColor: "rgba(245,245,240,0.85)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderColor: "rgba(0,0,0,0.06)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="contact-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_2d4aff0d-e5b7-449b-860f-09baa683a8bc/artifacts/b3uq1zlx_Logo.png"
              alt="Prysh AI"
              className="w-9 h-9 object-contain"
            />
            <span className="font-display text-lg font-semibold" style={{ color: "#1A1A1A" }}>Prysh AI</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
            style={{ color: "rgba(26,26,26,0.5)" }}
            data-testid="contact-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-32 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h1
            {...fade(0.1)}
            className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]"
            style={{ color: "#FF5A36" }}
            data-testid="contact-headline"
          >
            Get in Touch
          </motion.h1>
          <motion.p {...fade(0.2)} className="mt-5 text-base lg:text-lg max-w-xl mx-auto leading-relaxed" style={{ color: "rgba(26,26,26,0.5)" }}>
            Have a question, need support, or want to explore how Prysh can help your brand scale video ads? We're here to help.
          </motion.p>
        </div>
      </section>

      {/* ── INFO CARDS ── */}
      <section className="px-6 md:px-12 pb-16">
        <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Talk to Us */}
          <motion.div
            {...fade(0)}
            className="bg-white rounded-2xl p-8 text-center border"
            style={{ borderColor: "rgba(0,0,0,0.06)", boxShadow: "0 2px 12px rgba(0,0,0,0.03)" }}
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "rgba(255,90,54,0.1)" }}>
              <Mail className="w-6 h-6" style={{ color: "#FF5A36" }} />
            </div>
            <h3 className="font-display text-lg font-semibold mb-2" style={{ color: "#1A1A1A" }}>Talk to Us</h3>
            <a
              href="mailto:info@pryshai.com"
              className="text-sm font-medium hover:underline"
              style={{ color: "#FF5A36" }}
              data-testid="contact-email"
            >
              info@pryshai.com
            </a>
          </motion.div>

          {/* Quick Connect */}
          <motion.div
            {...fade(0.1)}
            className="bg-white rounded-2xl p-8 text-center border"
            style={{ borderColor: "rgba(0,0,0,0.06)", boxShadow: "0 2px 12px rgba(0,0,0,0.03)" }}
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "rgba(255,90,54,0.1)" }}>
              <Phone className="w-6 h-6" style={{ color: "#FF5A36" }} />
            </div>
            <h3 className="font-display text-lg font-semibold mb-2" style={{ color: "#1A1A1A" }}>Quick Connect</h3>
            <a
              href="tel:+919877559509"
              className="text-sm font-medium block mb-2"
              style={{ color: "rgba(26,26,26,0.6)" }}
              data-testid="contact-phone"
            >
              +91-9877559509
            </a>
            <a
              href="https://wa.me/919877559509"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
              style={{ color: "#25D366" }}
              data-testid="contact-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              Chat on WhatsApp
            </a>
          </motion.div>

          {/* Company Details */}
          <motion.div
            {...fade(0.2)}
            className="bg-white rounded-2xl p-8 text-center border"
            style={{ borderColor: "rgba(0,0,0,0.06)", boxShadow: "0 2px 12px rgba(0,0,0,0.03)" }}
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "rgba(255,90,54,0.1)" }}>
              <Building2 className="w-6 h-6" style={{ color: "#FF5A36" }} />
            </div>
            <h3 className="font-display text-lg font-semibold mb-2" style={{ color: "#1A1A1A" }}>Company</h3>
            <p className="text-sm font-medium" style={{ color: "rgba(26,26,26,0.6)" }}>Prysh AI Labs Pvt. Ltd.</p>
            <div className="flex items-start justify-center gap-1.5 mt-1">
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "rgba(26,26,26,0.35)" }} />
              <p className="text-xs leading-relaxed" style={{ color: "rgba(26,26,26,0.45)" }}>#308 GHS-34 Sector 20, Panchkula, Haryana - 134117</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section className="px-6 md:px-12 pb-20">
        <motion.div
          {...fade()}
          className="max-w-3xl mx-auto bg-white rounded-2xl p-10 md:p-14 text-center border"
          style={{ borderColor: "rgba(0,0,0,0.06)", boxShadow: "0 2px 12px rgba(0,0,0,0.03)" }}
        >
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-3" style={{ color: "#1A1A1A" }}>
            Want to See It in Action?
          </h2>
          <p className="text-base mb-8" style={{ color: "rgba(26,26,26,0.5)" }}>
            See how Prysh creates high-performing ad videos in minutes.
          </p>
          <Button
            onClick={() => navigate("/book-demo")}
            className="rounded-full px-10 py-6 text-base font-medium border-0 gap-2 transition-shadow hover:shadow-[0_8px_30px_rgba(255,90,54,0.25)]"
            style={{ backgroundColor: "#FF5A36", color: "#FFFFFF" }}
            data-testid="contact-book-demo-btn"
          >
            Book a Demo — Get 1 Free Ad
            <ArrowRight className="w-4 h-4" />
          </Button>
        </motion.div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="relative z-10 border-t py-12 px-6 md:px-12" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs" style={{ color: "rgba(26,26,26,0.25)" }}>
            &copy; {new Date().getFullYear()} Prysh AI Labs. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/about" className="text-xs font-medium" style={{ color: "rgba(26,26,26,0.4)" }}>About</Link>
            <Link to="/pricing-info" className="text-xs font-medium" style={{ color: "rgba(26,26,26,0.4)" }}>Pricing</Link>
            <Link to="/terms" className="text-xs font-medium" style={{ color: "rgba(26,26,26,0.4)" }}>Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
